import React, { useContext, useCallback, memo, useMemo } from "react";
import tinygradient from "tinygradient";
import {
  Row,
  IconWrapper,
  TextWrapper,
  Text,
} from "../styled/Tooltip.styled";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import { OP_MS_RADIO_DATA } from "../../constants/Data";
import { mapValueToRange } from "../../helpers/fuctionHelpers";

const TopTooltipInternal = ({
  dbmValue,
  uploadValue,
  downloadValue,
  rsrqValue,
  sinrValue,
  supplyType,
  rsrpLevel,
  downLevel,
  upLevel,
  rsrqLevel,
  sinrLevel,
  ms,
  quad,
  action,
  ms_rank,
  topMarketShareToggle,
  dataReceived,
  dataTransceived,
  dataTransmitted,
  type
}) => {
  const { msConfig } = useContext(WebMapContext);

  const renderRow = (iconSrc, text, value, backgroundColor) => (
    <Row key={text.label}>
      <div style={{ display: "flex", alignItems: "center", margin: "1px 0px" }}>
        <IconWrapper>
          <img src={iconSrc} alt="icon" width="13" />
        </IconWrapper>
        <Text isBold={text.isBold} style={{ marginRight: "10px" }}>{text.label}</Text>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "80px" }}>
      <div style={{ backgroundColor: backgroundColor, width: "13px", height: "13px", flexShrink: 0 }} />
      <TextWrapper style={{ flexShrink: 1, marginLeft: "4px" }}>{value}</TextWrapper>
      </div>
    </Row>
  );

  const msConfigColors = useCallback((msType) => {
    if (msConfig) {
      const section = msConfig[0]?.content.find((x) => x.type === msType);
      return section ? section.values : msConfig[0]?.content[0].values;
    }
    return null;
  }, [msConfig]);

  const gradient = useCallback((msType) => {
    const configColors = msConfigColors(msType);
    if (
      OP_MS_RADIO_DATA[7].value === msType &&
      configColors &&
      "colorGradient" in configColors[0]
    ) {
      return tinygradient(configColors[0].colorGradient);
    }
    return null;
  }, [msConfig]);

  const getLvlColor = (lvlVal, msType) => {
    const configColors = msConfigColors(msType);
    if (!configColors) return null;
    if (OP_MS_RADIO_DATA[7].value !== msType) {
      const colorObj = configColors.find((x) => x.colorText == lvlVal); // universal type matching
      return colorObj?.color;
    } else {
      const msPossibleRange = [0, 100]; // market share range is 0, 100
      if (gradient(msType)) {
        if (lvlVal >= msPossibleRange[0] && lvlVal <= msPossibleRange[1]) {
          return gradient(msType).rgbAt(Number(mapValueToRange(lvlVal, msPossibleRange[0], msPossibleRange[1])));
        }
      }
      return "gray";
    }
  };

  const quadLegendLabels = useMemo(() => {
    if (msConfig) {
      const labelMap = {};
      const section = msConfig[0]?.content.find((x) => x.type === "quad");
      if (section && section.values) {
        section.values.forEach(x => {
          labelMap[x.colorText] = x.text;
        })
      }
      return labelMap;
    }
    return {};
  }, [msConfig]);

  const fields = [
    {
      iconSrc: "/assets/signal.png",
      text: { label: "Signal Strength", isBold: (supplyType === "best_dbm" || supplyType === "l_rsrp") },
      value: dbmValue ? `${Number(dbmValue).toFixed(0)} dBm` : "No Data",
      backgroundColor: getLvlColor(rsrpLevel, "l_rsrp")
    },
    {
      iconSrc: "/assets/download.png",
      text: { label: "Download", isBold: (supplyType === "best_down" || supplyType === "l_down") },
      value: downloadValue ? `${Number(downloadValue).toFixed(2)} Mbps` : "No Data",
      backgroundColor: getLvlColor(downLevel, "l_down")
    },
    {
      iconSrc: "/assets/upload.png",
      text: { label: "Upload", isBold: (supplyType === "best_up" || supplyType === "l_up") },
      value: uploadValue ? `${Number(uploadValue).toFixed(2)} Mbps` : "No Data",
      backgroundColor: getLvlColor(upLevel, "l_up")
    },
    {
      iconSrc: "/assets/No Signal.png",
      text: { label: "Signal Quality", isBold: (supplyType === "best_rsrq" || supplyType === "l_rsrq") },
      value: rsrqValue ? `${Number(rsrqValue).toFixed(0)} dB` : "No Data",
      backgroundColor: getLvlColor(rsrqLevel, "l_rsrq")
    },
    {
      iconSrc: "/assets/sinrIcon.png",
      text: { label: "S/N Ratio", isBold: (supplyType === "best_sinr" || supplyType === "l_sinr") },
      value: sinrValue ? `${Number(sinrValue).toFixed(0)} dB` : "No Data",
      backgroundColor: getLvlColor(sinrLevel, "l_sinr")
    },
    ...(!topMarketShareToggle ? [{
      iconSrc: "/assets/quadrantIcon.png",
      text: { label: "Quadrant", isBold: supplyType === "quad" },
      value: quad && quadLegendLabels && quadLegendLabels[quad] ?  `${quadLegendLabels[quad]}` : "No Data",
      backgroundColor: getLvlColor(quad, "quad")
    },
    {
      iconSrc: "/assets/networkActionIcon.png",
      text: { label: "Network Action", isBold: supplyType === "action" },
      value: action ? `${action}` : "No Data",
      backgroundColor: getLvlColor(action, "action")
    }] : []),
    {
      iconSrc: "/assets/marketShareIcon.png",
      text: { label: "Market Share", isBold: (supplyType === "best_ms" || supplyType === "ms") },
      value: ms ? `${Number(ms).toFixed(0)} %` : "No Data",
      backgroundColor: getLvlColor(Number(ms)?.toFixed(0), "ms")
    },
    {
      iconSrc: "/assets/networkActionIcon.png",
      text: { label: "Data Received", isBold: (supplyType === "data_received") },
      value: dataReceived ? `${Number(dataReceived).toFixed(0)} MB` : "No Data",
      backgroundColor: "gray"
    },
    {
      iconSrc: "/assets/networkActionIcon.png",
      text: { label: "Data Transceived", isBold: (supplyType === "data_transceived") },
      value: dataTransmitted ? `${Number(dataTransmitted).toFixed(0)} MB` : "No Data",
      backgroundColor: "gray"
    },
    {
      iconSrc: "/assets/networkActionIcon.png",
      text: { label: "Data Transmitted", isBold: (supplyType === "data_transmitted") },
      value: dataTransceived ? `${Number(dataTransceived).toFixed(0)} MB` : "No Data",
      backgroundColor: "gray"
    },
    ...(!topMarketShareToggle ? [{
      iconSrc: "/assets/marketShareRankIcon.png",
      text: { label: "Market Share Rank", isBold: supplyType === "ms_rank" },
      value: ms_rank ? `${Number(ms_rank).toFixed(0)}` : "No Data",
      backgroundColor: getLvlColor(Number(ms_rank)?.toFixed(0), "ms_rank")
    }] : [])
  ];

  const getFilteredRows = () => {
    let rows = fields;
    if ((topMarketShareToggle && type === "selectedOperator") || (!topMarketShareToggle && type === "bestOperator")) {
      rows = fields.filter(x => x.text.isBold);
    }
    return rows;
  }

  return (
    <div>
      {getFilteredRows().map((row) =>
        renderRow(row.iconSrc, row.text, row.value, row.backgroundColor)
      )}
    </div>
  );
}

export default memo(TopTooltipInternal);
